import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import Headline from "~/components/Headline/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateStaffMembersContact } from "~/modules/humanResources/api/staffMember/staffMemberApiDispatchers.ts";
import type { StaffMember } from "~/modules/humanResources/api/staffMember/staffMemberTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type UpdateContactDetailsFormData = {
	emailCompany: string | null;
	emailPrivate: string | null;
	phone: string | null;
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	postCode: string | null;
	city: string | null;
	countryCode: string | null;
}

type UpdateContactDetailsFormProps = {
	onSuccess: () => void;
	onCancel: () => void;
	staffMemberCoreData: StaffMember;
};

const UpdateContactDetailsForm: React.FunctionComponent<UpdateContactDetailsFormProps> = ({
	onSuccess,
	onCancel,
	staffMemberCoreData,
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const countryCodeSelectOptions = useCountryCodeSelectOptions();
	const existingAddress = staffMemberCoreData.address;

	const schema = useMemo(() => {
		return yup.object({
			emailCompany: yup.string().nullable().email().default(null),
			emailPrivate: yup.string().nullable().email().default(null),
			phone: yup.string().nullable().default(null),
			addressLine1: yup.string().nullable().default(null),
			addressLine2: yup.string().nullable().default(null),
			addressLine3: yup.string().nullable().default(null),
			postCode: yup.string().nullable().default(null),
			city: yup.string().nullable().default(null),
			countryCode: yup.string().nullable().default(null),
		});
	}, []);

	const defaultValues = useMemo(() => {
		if (staffMemberCoreData) {
			return {
				emailCompany: staffMemberCoreData.emailCompany,
				emailPrivate: staffMemberCoreData.emailPrivate,
				phone: staffMemberCoreData.phone,
				addressLine1: existingAddress?.addressLine1 || "",
				addressLine2: existingAddress?.addressLine2 || "",
				addressLine3: existingAddress?.addressLine3 || "",
				postCode: existingAddress?.postCode || "",
				city: existingAddress?.city || "",
				countryCode: existingAddress?.countryCode || DEFAULT_COUNTRY_CODE,
			};
		}
		return {
			emailCompany: null,
			emailPrivate: null,
			phone: null,
			addressLine1: "",
			addressLine2: "",
			addressLine3: "",
			postCode: "",
			city: "",
			countryCode: DEFAULT_COUNTRY_CODE,
		};
	}, [staffMemberCoreData, existingAddress]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<UpdateContactDetailsFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateContactDetailsFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: UpdateContactDetailsFormData) => {
		const { addressLine1, addressLine2, addressLine3, postCode, city, countryCode, ...rest } = data;

		const hasAnyAddressField = addressLine1 || addressLine2 || addressLine3 || postCode || city || countryCode;
		const address = hasAnyAddressField
			? { addressLine1, addressLine2, addressLine3, postCode, city, countryCode }
			: null;

		try {
			setIsBusy(true);
			await updateStaffMembersContact(staffMemberCoreData.id, { ...rest, address });
			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			console.log(apiError);
			setServerErrorMessage("Ein unerwarteter Fehler ist aufgetreten.");
		}
	}, [onSuccess, staffMemberCoreData.id]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<Headline type="h4"
						  color="muted">
					Kontakt / Adresse
				</Headline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Kontaktdaten">
						<div className="grid grid-cols-2 gap-4">
							<Input control={control}
								   name="emailCompany"
								   label="E-Mail geschäftlich"
								   placeholder="vorname.nachname@nordnord.com"
							/>
							<Input control={control}
								   name="emailPrivate"
								   label="E-Mail privat"
								   placeholder="vorname.nachname@exmaple.com"
							/>
							<Input control={control}
								   name="phone"
								   label="Telefon"
								   placeholder="+49 123 4567890"
							/>
						</div>
					</FormSection>

					<AddressFormSection<UpdateContactDetailsFormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
						initialShowLine3={!!existingAddress?.addressLine3}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateContactDetailsForm;
